import type { AlgoliaProduct, TileType } from './types';
import type { SearchResponse } from '@algolia/client-search';
import type { BaseSyntheticEvent, FC, ReactNode, Ref } from 'react';

import type { Images, HideElements } from '~/amplienceTypes/schemas/exported/product-schema';
import type { ProductPrice } from '~/amplienceTypes/schemas/imported/product-price-schema';
import type {
  PrimaryImageType,
  HoverImageType,
} from '~/amplienceTypes/schemas/imported/product-recommendations-schema';
import { AlgoliaTileProvider } from '~/components/shared/recommendations/tile/AlgoliaTileProvider';
import { SolrTileProvider } from '~/components/shared/recommendations/tile/SolrTileProvider';
import { TileBase } from '~/components/shared/recommendations/tile/TileBase';
import type { ProductHit } from '~/types/algolia';
import type { RecentlyViewedProduct } from '~/utilities/helpers';

export interface TileProps {
  signingEnabled: boolean;
  product: AlgoliaProduct | RecentlyViewedProduct;
  showVariantSelector?: boolean;
  availableColorsLabel?: string;
  variants:
    | SearchResponse<ProductHit>['hits']
    | RecentlyViewedProduct['availableStyleVariantsSorted'];
  wishlistAction?: ReactNode;
  quickView?: ReactNode;
  tileType: TileType;
  productPrice?: ProductPrice;
  rel?: string;
  overrideImages?: Images;
  hideElements?: HideElements;
  primaryImageType?: PrimaryImageType;
  hoverImageType?: HoverImageType;
  onLinkClick?: (e: BaseSyntheticEvent) => void;
  onVariantLinkClick?: () => void;
  index?: number;
  aspectRatio?: number;
}

interface TileBaseProps extends TileProps {
  ref?: Ref<HTMLAnchorElement>;
  tabIndex?: number;
  tileContentOverlay?: ReactNode;
  productInfo?: ReactNode;
}

export const Tile: FC<TileBaseProps> = ({
  ref,
  tabIndex,
  product,
  tileContentOverlay,
  productInfo,
  ...restProps
}) => {
  const isSolrSource = Boolean((product as RecentlyViewedProduct)?.price?.value);

  return isSolrSource ? (
    <SolrTileProvider product={product as RecentlyViewedProduct} {...restProps}>
      <TileBase
        ref={ref}
        tabIndex={tabIndex}
        tileContentOverlay={tileContentOverlay}
        productInfo={productInfo}
      />
    </SolrTileProvider>
  ) : (
    <AlgoliaTileProvider product={product as AlgoliaProduct} {...restProps}>
      <TileBase
        ref={ref}
        tabIndex={tabIndex}
        tileContentOverlay={tileContentOverlay}
        productInfo={productInfo}
      />
    </AlgoliaTileProvider>
  );
};
